// ProfilePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/AboutPage.css";
import "../styles/HomePage.css";
import "../styles/ProfilePage.css";
import { getIdentifier, clearIdentifier } from "../utils/auth";
import { useAuth } from "../auth/useAuth";

const GET_USER_URL = "https://squid-app-6s2hc.ondigitalocean.app/get-user";
const UPDATE_USER_URL = "https://squid-app-6s2hc.ondigitalocean.app/update-user";
const DELETE_USER_URL = "https://squid-app-6s2hc.ondigitalocean.app/delete-user";

// ✅ adjust these if your routes differ
const ROUTE_CREATE_COLLECTION = "/upload-collection";
const ROUTE_VIEW_COLLECTIONS = "/view-collection-selector";
const ROUTE_EDIT_COLLECTIONS = "/edit-collection-selector";

const initialForm = {
  name: "",
  username: "",
  bio: "",
  location: "",
  interests: "",
  email: "",
  selectedOptions: [],
  avatarUrl: "",
   website: "",

  isBusiness: false,
  isVettedBusiness: false,
  allowCollectionContact: true,
  discoverableProfile: true,
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [original, setOriginal] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");
  const [showForm, setShowForm] = useState(false);
  const { logout } = useAuth();

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ✅ used for sidebar collection options
  const [hasCollection, setHasCollection] = useState(false);

  const identifier = useMemo(() => getIdentifier() || "", []);
  const idPayload = (id) => (id && id.includes("@") ? { email: id } : { username: id });

  const normalizeFromApi = (data, fallbackId) => ({
  name: data.name || "",
  username: data.username || "",
  bio: data.bio || "",
  location: data.location || "",
  interests: Array.isArray(data.interests)
    ? data.interests.join(", ")
    : data.interests || "",
  email: data.email || (fallbackId.includes("@") ? fallbackId : ""),
  selectedOptions: data.selectedOptions || [],
  avatarUrl: data.avatarUrl || "",
  website: data.website || "",

  isBusiness: data.isBusiness || false,
  isVettedBusiness: data.isVettedBusiness || false,
  allowCollectionContact:
  data.allowCollectionContact !== false,
  discoverableProfile:
    data.discoverableProfile !== false,
});

  // ✅ now returns { profile, hasCollection }
  const fetchUser = async (id) => {
    const res = await fetch(GET_USER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(idPayload(id)),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `Load failed (${res.status})`);

    const profile = normalizeFromApi(data, id);

    if (!profile.interests && Array.isArray(profile.selectedOptions) && profile.selectedOptions.length) {
      profile.interests = profile.selectedOptions.filter(Boolean).join(", ");
    }

    return { profile, hasCollection: !!data?.hasCollection };
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!identifier) {
        setLoading(false);
        return;
      }
      try {
        const { profile, hasCollection: hc } = await fetchUser(identifier);
        if (alive) {
          setForm(profile);
          setOriginal(profile);
          setHasCollection(hc);
        }
      } catch (e) {
        if (alive) setError(e.message || "Could not load profile");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [identifier]);

  useEffect(() => {
    if (!savedMsg) return;
    const t = setTimeout(() => setSavedMsg(""), 3000);
    return () => clearTimeout(t);
  }, [savedMsg]);

const changed =
  form.name !== original.name ||
  form.bio !== original.bio ||
  form.location !== original.location ||
  form.website !== original.website ||
  form.interests !== original.interests ||
  form.allowCollectionContact !== original.allowCollectionContact ||
  form.discoverableProfile !== original.discoverableProfile ||
  form.isBusiness !== original.isBusiness;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setSavedMsg("");
    setError("");
  };

  const onSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSavedMsg("");

      if (!form.username.trim()) throw new Error("Your username is missing.");

      const interestsArray = form.interests
        ? form.interests.split(",").map((s) => s.trim()).filter(Boolean)
        : [];

      const idForUpdate = form.email && form.email.includes("@") ? { email: form.email } : idPayload(identifier);

      const payload = {
          ...idForUpdate,
          name: form.name.trim(),
          bio: form.bio.trim(),
          location: form.location.trim(),
          interests: interestsArray,
          interestsString: form.interests,
          website: form.website.trim(),
          isBusiness: form.isBusiness,
          allowCollectionContact: form.allowCollectionContact,
          discoverableProfile: form.discoverableProfile,
        };

      const res = await fetch(UPDATE_USER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const bodyText = await res.text();
      const data = bodyText ? JSON.parse(bodyText) : {};
      if (!res.ok) throw new Error(data?.message || bodyText || "Failed to save profile");

      const { profile: fresh, hasCollection: hc } = await fetchUser(identifier);
      setForm(fresh);
      setOriginal(fresh);
      setHasCollection(hc);
      setShowForm(false);
    } catch (e) {
      setError(e.message || "Unable to save");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(original);
    setError("");
    setSavedMsg("");
    setShowForm(false);
  };

  const handleSignOut = async () => {
    try {
      if (logout) await logout();
    } finally {
      clearIdentifier();
      navigate("/welcome", { replace: true });
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const go = (path) => () => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const handleHome = go("/home-page");
  const handleDiscover = go("/discover");
  const handleProfile = go("/profile");
  const handleContact = go("/contact");

  // ✅ collections sidebar routes
  const handleCreateCollection = go(ROUTE_CREATE_COLLECTION);
  const handleViewCollections = go(ROUTE_VIEW_COLLECTIONS);
  const handleEditCollections = go(ROUTE_EDIT_COLLECTIONS);

  const openLearnMore = go("/about");

  const openDeleteConfirm = () => {
    setError("");
    setSavedMsg("");
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => setShowDeleteConfirm(false);

  const handleDeleteProfile = async () => {
    try {
      setDeleting(true);
      setError("");
      setSavedMsg("");

      const res = await fetch(DELETE_USER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(idPayload(identifier)),
      });

      const bodyText = await res.text();
      let data = {};
      try {
        data = bodyText ? JSON.parse(bodyText) : {};
      } catch {
        // ignore
      }

      if (!res.ok) throw new Error(data?.message || bodyText || "Failed to delete profile");

      clearIdentifier();
      setShowDeleteConfirm(false);
      setShowForm(false);
      navigate("/welcome", { replace: true });
    } catch (e) {
      setError(e.message || "Unable to delete profile");
    } finally {
      setDeleting(false);
    }
  };

  const interestsList =
    (form.interests ? form.interests.split(",").map((s) => s.trim()).filter(Boolean) : []) || [];
  const fallbackList = Array.isArray(form.selectedOptions) ? form.selectedOptions.filter(Boolean) : [];
  const previewInterests = interestsList.length ? interestsList : fallbackList;
  const previewInterestsText = previewInterests.join(", ");

  const avatarLetter =
    (form.name || "").trim().slice(0, 1) ||
    (form.username || "").trim().slice(0, 1) ||
    "•";

  return (
    <div className={`nav-shell ${mobileNavOpen ? "nav-shell--open" : ""}`}>
      <div
  className={`menu-overlay ${mobileNavOpen ? "open" : ""}`}
  onClick={() => setMobileNavOpen(false)}
/>

<aside className={`slide-menu ${mobileNavOpen ? "open" : ""}`}>
  <button
    className="menu-close"
    onClick={() => setMobileNavOpen(false)}
  >
    ×
  </button>

  <nav className="menu-nav">

    <button onClick={handleHome}>
      home
    </button>

    <div className="menu-divider" />

    <button onClick={handleCreateCollection}>
      create new collection
    </button>

    {hasCollection && (
      <>
        <button onClick={handleViewCollections}>
          view my collection(s)
        </button>

        <button onClick={handleEditCollections}>
          edit my collection(s)
        </button>
      </>
    )}

    <div className="menu-divider" />


    <button onClick={handleDiscover}>
      browse collections
    </button>


    <button onClick={openLearnMore}>
      learn more
    </button>

    <button onClick={handleContact}>
      contact t.c.p.
    </button>

  </nav>
</aside>
     
       {/* ✅ Burger OUTSIDE main so it doesn't get pushed by layout */}
    {!mobileNavOpen && (
  <header className="nav-burger-wrap" aria-label="page controls">

    <button
      type="button"
      className="nav-burger"
      onClick={() => setMobileNavOpen(true)}
      aria-label="Open menu"
    >
      ☰
    </button>

   <div className="profile-top-actions">

    <button
      className="profile-header-link"
      onClick={handleSignOut}
    >
      sign out
    </button>

    <button
      className="profile-header-link delete-link"
      onClick={openDeleteConfirm}
    >
      delete profile
    </button>

  </div>
  </header>
)}
      

      <main className="profile-page" role="main">
      
        <section className="panel active panel-elevated profile-preview centered-panel">
          <div className="preview-top">

<div className="avatar">
  {form.avatarUrl ? (
    <img
      src={form.avatarUrl}
      alt={`${form.name || form.username} avatar`}
    />
  ) : (
    <div className="avatar-fallback">
      {avatarLetter}
    </div>
  )}
</div>
  <div className="profile-heading">

    <div className="profile-name">
      {form.name}
    </div>

    <div className="public-username">
      @{form.username}

      {form.isBusiness && (
        <span className="business-status">
          BUSINESS · {form.isVettedBusiness ? "VETTED" : "NOT VETTED"}
        </span>
      )}
    </div>

  </div>

</div>
       
        <div className="profile-meta-group">

  {form.location && (
    <div className="profile-meta">
      📍 {form.location}
    </div>
  )}

  {form.website && (
    <div className="profile-meta">
      💻 {form.website}
    </div>
  )}

</div>

{previewInterests.length > 0 && (
 <div className="collector-section">

  <div className="section-heading">
    collects
  </div>

  <div className="collector-items">
    {previewInterests.map((interest) => (
      <div key={interest}>
        {interest}
      </div>
    ))}
  </div>

</div>
)}

{form.bio?.trim().length > 0 && (
  <>
    <div className="section-heading">
      the collector's story
    </div>

    <p className="public-bio">
      {form.bio}
    </p>
  </>
)}

          <div className="preview-actions">
            <button
              className="community-cta outline collections-cta"
              onClick={() => navigate(ROUTE_VIEW_COLLECTIONS)}
            >
              view my collections
            </button>

            <button
              className="community-cta primary"
              onClick={() => setShowForm((s) => !s)}
              aria-expanded={showForm}
            >
              {showForm ? "done" : "edit profile"}
            </button>
          </div>
        </section>

        {showForm && (
          <section className="panel active panel-elevated profile-editor centered-panel" aria-label="edit profile">
            {loading ? (
              <p className="muted">loading…</p>
            ) : (
              <form className="profile-form" onSubmit={(e) => e.preventDefault()}>
                <div className="row two">
                  <label>
                    <span>name</span>
                    <input
                      name="name"
                      type="text"
                      value={form.name}
                      onChange={onChange}
                      maxLength={60}
                    />
                  </label>

                  <label>
                    <span>username</span>
                    <input name="username" type="text" value={form.username} readOnly className="read-only" />
                  </label>
                </div>

                <div className="row two">
                  <label>
                    <span>location</span>
                    <input
                      name="location"
                      type="text"
                      value={form.location}
                      onChange={onChange}
                      maxLength={80}
                    />
                  </label>


                  <label>
                    <span>email</span>
                    <input name="email" type="email" value={form.email} readOnly className="read-only" />
                  </label>
                </div>

                 <label className="row one">
                  <span>interests</span>
                  <input
                    name="interests"
                    type="text"
                    value={form.interests}
                    onChange={onChange}
                    placeholder="beanie babies, photobooks, postage stamps"
                  />
                </label>
           
                <label className="row one">
                  <span>bio</span>
                  <textarea
                    name="bio"
                    rows={4}
                    value={form.bio}
                    onChange={onChange}
                    placeholder="tell us your collecting story… or whatever you’d like"
                    maxLength={600}
                  />
                </label>

                                     <label>
  <span>website</span>
  <input
    name="website"
    type="text"
    value={form.website}
    onChange={onChange}
    placeholder="https://example.com"
    maxLength={200}
  />
</label>

       <div
  className={`profile-option ${
    form.allowCollectionContact ? "active" : ""
  }`}
  onClick={() =>
    setForm((f) => ({
      ...f,
      allowCollectionContact: !f.allowCollectionContact,
    }))
  }
>
  <span className="option-mark">
    {form.allowCollectionContact ? "✓" : "○"}
  </span>

  <span>
    i'm happy to be contacted about my collection
  </span>
</div>

<div
  className={`profile-option ${
    form.discoverableProfile ? "active" : ""
  }`}
  onClick={() =>
    setForm(f => ({
      ...f,
      discoverableProfile: !f.discoverableProfile
    }))
  }
>
  <span className="option-mark">
    {form.discoverableProfile ? "✓" : "○"}
  </span>

  <span>
    make my profile discoverable
  </span>
</div>
<div
  className={`profile-option ${
    form.isBusiness ? "active" : ""
  }`}
  onClick={() =>
    setForm((f) => ({
      ...f,
      isBusiness: !f.isBusiness,
    }))
  }
>
  <span className="option-mark">
    {form.isBusiness ? "✓" : "○"}
  </span>

  <span>
    are you a business?
  </span>
</div>

{form.isBusiness && (
  <div className="profile-note">
    please email shaq@thecollectorsplatform.com to be vetted.
  </div>
)}



                <div className="form-actions">
                  {savedMsg && <span className="saved-msg">{savedMsg}</span>}
                  {error && <span className="error-message">{error}</span>}

                  <button
                    type="button"
                    className="community-cta"
                    onClick={handleCancel}
                    disabled={saving || deleting}
                  >
                    cancel
                  </button>

                  <button
                    type="button"
                    className="community-cta primary"
                    onClick={onSave}
                    disabled={!changed || saving || deleting}
                    title={!changed ? "no changes" : ""}
                  >
                    {saving ? "saving…" : "save changes"}
                  </button>

                </div>
              </form>
            )}
          </section>
        )}

        {showDeleteConfirm && (
          <div
            className="terminal-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="confirm delete profile"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeDeleteConfirm();
            }}
          >
            <div className="terminal-modal" style={{ width: "min(92vw, 560px)" }}>
              <button className="modal-close" onClick={closeDeleteConfirm} aria-label="close">
                ×
              </button>

              <div className="modal-body">
                <div className="modal-title">delete profile?</div>
                <div className="modal-copy">
                  this will permanently delete your profile (and anything tied to it). this cannot be undone.
                </div>

                <div className="modal-actions">
                  <button type="button" className="community-cta" onClick={closeDeleteConfirm} disabled={deleting}>
                    keep my profile
                  </button>

                  <button
                    type="button"
                    className="community-cta danger"
                    onClick={handleDeleteProfile}
                    disabled={deleting}
                  >
                    {deleting ? "deleting…" : "yes, delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
